import { useEffect, useState } from 'react'
import type { ScenarioResultResponse } from '@/types/api'

const SCENARIO_RESULTS_KEY = 'scenario_results'

interface UseScenarioResultsReturn {
  data: ScenarioResultResponse | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * Hook to access scenario results from sessionStorage.
 * Results are passed from /scenarios page when a scenario is submitted.
 * If user navigates directly to /results without running a scenario,
 * returns null with appropriate error state.
 */
export function useScenarioResults(): UseScenarioResultsReturn {
  const [data, setData] = useState<ScenarioResultResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(SCENARIO_RESULTS_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as ScenarioResultResponse
        setData(parsed)
        setError(null)
      } else {
        setData(null)
        setError(new Error('No scenario results available. Run a scenario first.'))
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load scenario results'))
      setData(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const refetch = () => {
    setIsLoading(true)
    try {
      const stored = sessionStorage.getItem(SCENARIO_RESULTS_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as ScenarioResultResponse
        setData(parsed)
        setError(null)
      } else {
        setData(null)
        setError(new Error('No scenario results available. Run a scenario first.'))
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load scenario results'))
      setData(null)
    } finally {
      setIsLoading(false)
    }
  }

  return { data, isLoading, error, refetch }
}

/**
 * Store scenario results in sessionStorage.
 * Called from /scenarios page after successful API submission.
 */
export function storeScenarioResults(results: ScenarioResultResponse): void {
  try {
    sessionStorage.setItem(SCENARIO_RESULTS_KEY, JSON.stringify(results))
  } catch (err) {
    console.error('Failed to store scenario results:', err)
  }
}

/**
 * Clear scenario results from sessionStorage.
 * Called when user wants to start fresh.
 */
export function clearScenarioResults(): void {
  try {
    sessionStorage.removeItem(SCENARIO_RESULTS_KEY)
  } catch (err) {
    console.error('Failed to clear scenario results:', err)
  }
}
